import React, { useEffect, useMemo, useState } from "react";
import {
  FaSearch,
  FaFileExcel,
  FaStore,
  FaCalendarAlt,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
  FaUserFriends,
  FaMoneyCheckAlt,
  FaExclamationCircle,
  FaClock,
} from "react-icons/fa";
import { axiosMaster, axiosSalary } from "../../api/axiosInstance";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// ---------- small helpers ----------

const minutesToHm = (mins) => {
  const m = Number(mins) || 0;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0 && rem === 0) return "0m";
  if (h === 0) return `${rem}m`;
  return `${h}h ${rem}m`;
};

const StatusBadge = ({ status }) => {
  const isGenerated = status === 1;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
        isGenerated
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
          : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          isGenerated ? "bg-emerald-500" : "bg-amber-500"
        }`}
      />
      {isGenerated ? "Generated" : "Pending"}
    </span>
  );
};

// Stacked visual bar: full / half / quarter / absent days out of workingDays
const AttendanceBar = ({ row }) => {
  const total = Math.max(Number(row.workingDays) || 0, 1);
  const segments = [
    { key: "full", value: row.fullDays, color: "bg-emerald-500" },
    { key: "half", value: row.halfDays, color: "bg-sky-400" },
    { key: "quarter", value: row.quarterDays, color: "bg-violet-400" },
    { key: "absent", value: row.absentDays, color: "bg-rose-400" },
  ];

  return (
    <div className="w-full min-w-[140px]">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
        {segments.map((s) =>
          s.value > 0 ? (
            <div
              key={s.key}
              className={s.color}
              style={{ width: `${(s.value / total) * 100}%` }}
              title={`${s.key}: ${s.value}`}
            />
          ) : null
        )}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-2 text-[11px] text-slate-400">
        <span>Full {row.fullDays}</span>
        <span>Half {row.halfDays}</span>
        <span>Qtr {row.quarterDays}</span>
        <span>Absent {row.absentDays}</span>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, sub, accent }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
    <div
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${accent}`}
    >
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-lg font-semibold leading-tight text-slate-800">
        {value}
      </p>
      <p className="truncate text-xs text-slate-500">{label}</p>
      {sub ? <p className="text-[11px] text-slate-400">{sub}</p> : null}
    </div>
  </div>
);

const MonthlyAttendanceSummaryReport = () => {
  let storedUser = {};

  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const groupId = storedUser?.groupId;

  const currentDate = new Date();

  const [restaurants, setRestaurants] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [reportData, setReportData] = useState([]);

  const [selectedRestId, setSelectedRestId] = useState("");
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchRestaurants = async () => {
    try {
      const res = await axiosMaster.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );
      const rows = res.data || [];
      setRestaurants(rows);
      if (rows.length > 0) {
        setSelectedRestId(rows[0].restId);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axiosMaster.get(
        `/EmployeeMaster/GetByGroup?groupId=${groupId}`
      );
      setEmployees(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchRestaurants();
    fetchEmployees();
  }, []);

  const fetchReport = async () => {
    if (!selectedRestId) {
      alert("Please select restaurant");
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);

      const res = await axiosSalary.get(
        `/MonthlyAttendanceSummary/GetByRestaurant?groupId=${groupId}&restId=${selectedRestId}&month=${month}&year=${year}`
      );

      setReportData(res.data || []);
      setExpandedRow(null);
    } catch (err) {
      console.error(err);
      alert("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find((e) => Number(e.empId) === Number(empId));
    return emp?.empName || "-";
  };

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return reportData;
    const term = searchTerm.trim().toLowerCase();
    return reportData.filter((row) => {
      const name = getEmployeeName(row.empId).toLowerCase();
      return name.includes(term) || String(row.empId).includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData, searchTerm, employees]);

  const summary = useMemo(() => {
    const totalEmployees = reportData.length;
    const totalPaidDays = reportData.reduce(
      (sum, r) => sum + (Number(r.paidDays) || 0),
      0
    );
    const totalAbsentDays = reportData.reduce(
      (sum, r) => sum + (Number(r.absentDays) || 0),
      0
    );
    const totalOtMinutes = reportData.reduce(
      (sum, r) => sum + (Number(r.overtimeMinutes) || 0),
      0
    );
    const pendingCount = reportData.filter(
      (r) => r.summaryStatus !== 1
    ).length;

    return {
      totalEmployees,
      totalPaidDays,
      totalAbsentDays,
      totalOtMinutes,
      pendingCount,
    };
  }, [reportData]);

  const exportExcel = () => {
    if (!reportData.length) {
      alert("No data found");
      return;
    }

    const excelData = reportData.map((row) => ({
      EmployeeId: row.empId,
      EmployeeName: getEmployeeName(row.empId),
      PaidDays: row.paidDays,
      WorkingDays: row.workingDays,
      FullDays: row.fullDays,
      HalfDays: row.halfDays,
      QuarterDays: row.quarterDays,
      AbsentDays: row.absentDays,
      LeaveWithoutPay: row.leaveWithoutPay,
      AttendanceCredit: row.attendanceCredit,
      WorkingMinutes: row.workingMinutes,
      OvertimeMinutes: row.overtimeMinutes,
      LateCount: row.lateCount,
      LateMinutes: row.lateMinutes,
      EarlyLeaveCount: row.earlyLeaveCount,
      EarlyLeaveMinutes: row.earlyLeaveMinutes,
      Remarks: row.remarks,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, `MonthlyAttendanceSummary_${month}_${year}.xlsx`);
  };

  const toggleExpand = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  return (
    <div className="min-h-[calc(100vh-120px)] rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* Header */}
      <div className="border-b border-slate-100 px-6 py-5">
        <h2 className="text-xl font-bold text-slate-800">
          Monthly Attendance Summary Report
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          View and export monthly attendance summary report.
        </p>
      </div>

      {/* Filters */}
      <div className="border-b border-slate-100 px-6 py-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <div>
            <label className="flex items-center gap-1 text-xs font-medium text-slate-500">
              <FaStore />
              Restaurant
            </label>
            <select
              value={selectedRestId}
              onChange={(e) => setSelectedRestId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            >
              {restaurants.map((r) => (
                <option key={r.restId} value={r.restId}>
                  {r.restaurantName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-1 text-xs font-medium text-slate-500">
              <FaCalendarAlt />
              Month
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2025, i, 1).toLocaleString("default", {
                    month: "long",
                  })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500">
              Year
            </label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            >
              {[2025, 2026, 2027, 2028].map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchReport}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <FaSyncAlt className="animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FaSearch />
                  Search
                </>
              )}
            </button>
          </div>

          <div className="flex items-end">
            <button
              onClick={exportExcel}
              disabled={!reportData.length}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:bg-emerald-300"
            >
              <FaFileExcel />
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* Summary stat cards */}
      {reportData.length > 0 && (
        <div className="grid grid-cols-2 gap-3 px-6 py-5 md:grid-cols-4">
          <StatCard
            icon={<FaUserFriends className="text-indigo-600" />}
            accent="bg-indigo-50"
            label="Employees in report"
            value={summary.totalEmployees}
          />
          <StatCard
            icon={<FaMoneyCheckAlt className="text-emerald-600" />}
            accent="bg-emerald-50"
            label="Total paid days"
            value={summary.totalPaidDays}
          />
          <StatCard
            icon={<FaExclamationCircle className="text-rose-600" />}
            accent="bg-rose-50"
            label="Total absent days"
            value={summary.totalAbsentDays}
            sub={
              summary.pendingCount > 0
                ? `${summary.pendingCount} summary(s) pending`
                : "All summaries generated"
            }
          />
          <StatCard
            icon={<FaClock className="text-violet-600" />}
            accent="bg-violet-50"
            label="Total overtime"
            value={minutesToHm(summary.totalOtMinutes)}
          />
        </div>
      )}

      {/* Table */}
      <div className="px-6 pb-6">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-slate-500">
            Showing {filteredData.length} of {reportData.length} record
            {reportData.length === 1 ? "" : "s"}
          </div>
          {reportData.length > 0 && (
            <input
              type="text"
              placeholder="Search employee name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 sm:w-72"
            />
          )}
        </div>

        <div className="overflow-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-slate-500">
              <tr>
                <th className="p-3 text-left font-medium">Employee</th>
                <th className="p-3 text-left font-medium">Attendance</th>
                <th className="p-3 text-left font-medium">Paid Days</th>
                <th className="p-3 text-left font-medium">LWP</th>
                <th className="p-3 text-left font-medium">Overtime</th>
                <th className="p-3 text-left font-medium">Late</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium"></th>
              </tr>
            </thead>

            <tbody>
              {!hasSearched ? (
                <tr>
                  <td colSpan="8" className="py-14 text-center text-slate-400">
                    Select a restaurant, month and year, then click Search.
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-14 text-center text-slate-400">
                    No records found
                  </td>
                </tr>
              ) : (
                filteredData.map((row) => {
                  const isOpen = expandedRow === row.id;
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        onClick={() => toggleExpand(row.id)}
                        className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                      >
                        <td className="p-3">
                          <div className="font-medium text-slate-800">
                            {getEmployeeName(row.empId)}
                          </div>
                          <div className="text-xs text-slate-400">
                            ID {row.empId}
                          </div>
                        </td>
                        <td className="p-3">
                          <AttendanceBar row={row} />
                        </td>
                        <td className="p-3">
                          <span className="font-medium text-slate-700">
                            {row.paidDays}
                          </span>
                          <span className="text-slate-400">
                            {" "}
                            / {row.workingDays}
                          </span>
                        </td>
                        <td className="p-3 text-rose-600">
                          {row.leaveWithoutPay}
                        </td>
                        <td className="p-3">
                          {minutesToHm(row.overtimeMinutes)}
                        </td>
                        <td className="p-3">
                          {row.lateCount > 0 ? (
                            <span className="text-amber-600">
                              {row.lateCount}x
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <StatusBadge status={row.summaryStatus} />
                        </td>
                        <td className="p-3 text-slate-400">
                          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="border-t border-slate-100 bg-slate-50/60">
                          <td colSpan="8" className="px-4 py-4">
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs sm:grid-cols-4 lg:grid-cols-6">
                              <Detail
                                label="Working Minutes"
                                value={minutesToHm(row.workingMinutes)}
                              />
                              <Detail
                                label="Late Minutes"
                                value={minutesToHm(row.lateMinutes)}
                              />
                              <Detail
                                label="Early Leave Count"
                                value={row.earlyLeaveCount}
                              />
                              <Detail
                                label="Early Leave Minutes"
                                value={minutesToHm(row.earlyLeaveMinutes)}
                              />
                              <Detail
                                label="Leave Credit (used/allowed)"
                                value={`${row.leaveCreditUsed}/${row.leaveCreditAllowed}`}
                              />
                              <Detail
                                label="Unused Leave Credit"
                                value={row.unusedLeaveCredit}
                              />
                              <Detail
                                label="Attendance Credit"
                                value={row.attendanceCredit}
                              />
                              <Detail
                                label="Weekly Off / Holiday"
                                value={`${row.weeklyOffDays} / ${row.holidayDays}`}
                              />
                              <Detail
                                label="Salary Mode"
                                value={row.salaryCalculationMode}
                              />
                              <Detail
                                label="Generated On"
                                value={
                                  row.generatedOn
                                    ? new Date(
                                        row.generatedOn
                                      ).toLocaleDateString()
                                    : "-"
                                }
                              />
                              <Detail
                                label="Remarks"
                                value={row.remarks || "-"}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <p className="text-slate-400">{label}</p>
    <p className="font-medium text-slate-700">{value}</p>
  </div>
);

export default MonthlyAttendanceSummaryReport;